import { useAppSelector } from "src/redux/hooks";
import type { LineOfBusiness } from "src/app/types";
import { LINE_OF_BUSINESS_BY_RESOLVE_INPUT } from "../attachments";

/** Where a claim's attachments are uploaded, resolved from pre-screen state. */
export interface S3AttachmentTarget {
  /** S3 key prefix shared by every attachment on the current claim. */
  s3AttachmentsId: string | null;
  /** Selects the upload bucket and the file size caps. */
  lineOfBusiness: LineOfBusiness | null;
}

/**
 * Resolves the upload target for the claim currently being filled in.
 *
 * Both values come from the pre-screen: `s3AttachmentsId` is minted when a
 * pre-screen is accepted, and the line of business is derived from the
 * resolve-schema input that produced the rendered schema. Either can be `null`
 * if the form somehow renders without a pre-screen behind it, which callers
 * must treat as "cannot upload" rather than substituting a default — a wrong
 * prefix would scatter a claim's files across folders, and a wrong line of
 * business would apply the wrong size caps and bucket.
 */
export function useS3AttachmentTarget(): S3AttachmentTarget {
  const s3AttachmentsId = useAppSelector((state) => state.jsonForms.preScreen.s3AttachmentsId);
  const resolveSchemaInput = useAppSelector(
    (state) => state.jsonForms.preScreen.resolveSchemaInput
  );

  return {
    s3AttachmentsId,
    lineOfBusiness: resolveSchemaInput
      ? (LINE_OF_BUSINESS_BY_RESOLVE_INPUT[resolveSchemaInput.lineOfBusiness] ?? null)
      : null,
  };
}
